'use client';

import { BodySecondary, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { DestinationMarketplaceValue, MARKETPLACES } from '@/types/auction';
import { House, HouseFeature } from '@/types/house.types';
import { LoadingSpinner } from '@liveauctioneers/hammer-ui-core/loadingSpinner';
import { MasterToggleCheckboxGroup } from '@/components/Shared/MasterToggleCheckboxGroup/MasterToggleCheckboxGroup';
import { nextEnabledFeatures } from './nextEnabledFeatures';
import { omitRemovedDestinationMarketplaces } from '@/utils/marketplace/omitRemovedDestinationMarketplaces';
import { PartnerNetworkEnablementGroup } from './PartnerNetworkEnablementGroup';
import { QueryKey } from '@/data/enums/queryKeys';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { useGetEnabledDestinationMarketplaces } from '@/data/house/useGetEnabledDestinationMarketplaces/useGetEnabledDestinationMarketplaces';
import { useGetHouseById } from '@/data/house/useGetHouseById';
import { usePutEnabledDestinationMarketplaces } from '@/data/house/usePutEnabledDestinationMarketplaces/usePutEnabledDestinationMarketplaces';
import { usePutEnabledFeatures } from '@/data/house/usePutEnabledFeatures/usePutEnabledFeatures';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslations } from 'next-intl';

type CrossListingConfigPanelProps = Readonly<{
    houseId: string;
    id: string;
    isOpen: boolean;
}>;

const MARKETPLACE_OPTIONS = [MARKETPLACES.GAP_BSC, MARKETPLACES.GAP_PBWL, MARKETPLACES.LA_MP, MARKETPLACES.PXB_MP].map(
    ({ code, label }) => ({ label, value: code })
);

export const CrossListingConfigPanel = ({ houseId, id, isOpen }: CrossListingConfigPanelProps) => {
    const masterMutationInFlight = useRef(false);
    const optionsMutationInFlight = useRef(false);
    const t = useTranslations('AdminToolbar.crossListing');
    const houseQuery = useGetHouseById(houseId);
    const allowlistQuery = useGetEnabledDestinationMarketplaces({ enabled: isOpen });
    const putEnabledFeatures = usePutEnabledFeatures();
    const putEnabledDestinations = usePutEnabledDestinationMarketplaces();
    const queryClient = useQueryClient();

    if (!isOpen) {
        return null;
    }

    const enabledFeatures = houseQuery.data?.enabledFeatures;
    const selectedValues = allowlistQuery.data?.enabledDestinationMarketplaces ?? [];
    const isMasterEnabled = enabledFeatures?.includes(HouseFeature.CROSS_LISTING) ?? false;
    const isMasterDisabled = !houseQuery.isSuccess || enabledFeatures === undefined || putEnabledFeatures.isPending;
    const areOptionsDisabled =
        !allowlistQuery.isSuccess || putEnabledDestinations.isPending || putEnabledFeatures.isPending;

    const handleMasterToggle = async (crossListing: boolean) => {
        if (isMasterDisabled || masterMutationInFlight.current) {
            return;
        }
        const currentFeatures = queryClient.getQueryData<House>([QueryKey.House, houseId])?.enabledFeatures;
        if (currentFeatures === undefined) {
            return;
        }

        masterMutationInFlight.current = true;
        try {
            await putEnabledFeatures.mutateAsync({
                enabledFeatures: nextEnabledFeatures(currentFeatures, { crossListing }),
            });
            toast('success', { title: t('toastSuccess') });
        } catch {
            // Error feedback and optimistic rollback are owned by the mutation hook.
        } finally {
            masterMutationInFlight.current = false;
        }
    };

    const handleOptionToggle = async (value: DestinationMarketplaceValue, isChecked: boolean) => {
        if (areOptionsDisabled || optionsMutationInFlight.current) {
            return;
        }
        const current = queryClient.getQueryData<{ enabledDestinationMarketplaces: DestinationMarketplaceValue[] }>([
            QueryKey.EnabledDestinationMarketplaces,
            houseId,
        ])?.enabledDestinationMarketplaces;
        if (current === undefined) {
            return;
        }

        const enabledDestinationMarketplaces = omitRemovedDestinationMarketplaces(
            isChecked
                ? [...current.filter((destination) => destination !== value), value]
                : current.filter((destination) => destination !== value)
        );

        optionsMutationInFlight.current = true;
        try {
            await putEnabledDestinations.mutateAsync({ enabledDestinationMarketplaces });
            toast('success', { title: t('toastSuccess') });
        } catch {
            // Error feedback and optimistic rollback are owned by the mutation hook.
        } finally {
            optionsMutationInFlight.current = false;
        }
    };

    return (
        <dialog
            aria-labelledby={`${id}-title`}
            className="absolute top-full right-0 left-auto z-50 m-0 mt-xs w-[356px] rounded-md bg-background-primary p-md text-text-primary shadow-04"
            data-testid="cross-listing-config-panel"
            id={id}
            open
        >
            <H6
                className="mb-sm uppercase"
                color="secondary"
                id={`${id}-title`}
            >
                {t('title')}
            </H6>
            <div
                aria-live="polite"
                data-testid="cross-listing-config-status"
            >
                {(houseQuery.isPending || allowlistQuery.isPending) && (
                    <div className="mb-sm flex justify-center">
                        <LoadingSpinner data-testid="cross-listing-config-loading" />
                    </div>
                )}
                {houseQuery.isError && (
                    <BodySecondary className="mb-sm text-text-critical">{t('houseError')}</BodySecondary>
                )}
                {allowlistQuery.isError && (
                    <BodySecondary className="mb-sm text-text-critical">{t('allowlistError')}</BodySecondary>
                )}
            </div>
            <MasterToggleCheckboxGroup
                areOptionsDisabled={areOptionsDisabled}
                data-testid="cross-listing-config-controls"
                isMasterDisabled={isMasterDisabled}
                isMasterEnabled={isMasterEnabled}
                masterLabel={t('masterLabel')}
                onMasterToggle={(isEnabled) => void handleMasterToggle(isEnabled)}
                onOptionToggle={(value, isChecked) => void handleOptionToggle(value, isChecked)}
                options={MARKETPLACE_OPTIONS}
                selectedValues={selectedValues}
            />
            <PartnerNetworkEnablementGroup
                disabled={areOptionsDisabled || !isMasterEnabled}
                onToggle={handleOptionToggle}
                selectedValues={selectedValues}
            />
        </dialog>
    );
};

CrossListingConfigPanel.displayName = 'CrossListingConfigPanel';
